// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@conklin/App/Widgets/DesignSystem/DesignSystem.helpers";
import { dummyProductContext } from "@conklin/App/Widgets/DesignSystem/DummyData/DummyProduct";
import IconArrowRight from "@conklin/XDL/Icons/IconArrowRight";
import IconEdit from "@conklin/XDL/Icons/IconEdit";
import { createLinkIconStyles } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import Button, { ButtonVariants } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

const buttonVariants: ButtonVariants[] = ["primary", "secondary"];

interface ButtonsTabStyles {
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
}

const buttonsTabStyles: ButtonsTabStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: auto;
            margin-top: 20px;
            gap: 12px;
            align-items: flex-start;

            ${({ black }: any) =>
                black &&
                css`
                    background: #373636;
                    padding: 8px;
                    border-radius: 10px;
                `}
        `,
    },
};

const styles = buttonsTabStyles;
const ButtonsTab: FC = props => {
    return (
        <SectionContainer>
            {buttonVariants.map(buttonVariant => (
                <GridItem key={buttonVariant} {...styles.smallGridItem}>
                    <SectionTitle>{`Button : ${capitalize(buttonVariant)}`}</SectionTitle>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button sizeVariant="small" variant={buttonVariant}>
                            {translate("Button : Small")}
                        </Button>
                        <Button sizeVariant="medium" variant={buttonVariant}>
                            {translate("Button : Medium")}
                        </Button>

                        <Button
                            sizeVariant="large"
                            variant={buttonVariant}
                            icon={{ src: IconArrowRight, position: "right" }}
                        >
                            {translate("Button : Large")}
                        </Button>
                        <Button disabled sizeVariant="medium" variant={buttonVariant}>
                            {translate("Button : Disabled")}
                        </Button>
                    </StyledWrapper>
                </GridItem>
            ))}
            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Add To Cart Button")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <ProductContext.Provider value={dummyProductContext}>
                        <ProductAddToCartButton data-test-selector="actionsAddToCart-545454654" />
                    </ProductContext.Provider>
                    <ProductContext.Provider value={dummyProductContext}>
                        <ProductAddToCartButton
                            data-test-selector="actionsAddToCart-545454654"
                            {...{ isIconButton: true }}
                        />
                    </ProductContext.Provider>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>{translate("UI: Icon Links : Big")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Link href="/" {...createLinkIconStyles(IconEdit)}>
                        Text Example
                    </Link>
                    <Link href="/" {...createLinkIconStyles(IconEdit)} disabled={true}>
                        Text Example
                    </Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>{translate("UI: Icon Links : Small")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Link href="/" {...createLinkIconStyles(IconEdit, "small")}>
                        Text Example
                    </Link>
                    <Link href="/" {...createLinkIconStyles(IconEdit, "small")} disabled={true}>
                        Text Example
                    </Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>{translate("UI: Icon Links : Big : White")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper} {...{ black: true }}>
                    <Link href="/" {...createLinkIconStyles(IconEdit, "default", "left", true)}>
                        Text Example
                    </Link>
                    <Link href="/" {...createLinkIconStyles(IconEdit, "default", "left", true)} disabled={true}>
                        Text Example
                    </Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>{translate("UI: Icon Links : Small : White")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper} {...{ black: true }}>
                    <Link href="/" {...createLinkIconStyles(IconEdit, "small", "left", true)}>
                        Text Example
                    </Link>
                    <Link href="/" {...createLinkIconStyles(IconEdit, "small", "left", true)} disabled={true}>
                        Text Example
                    </Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Upload File Button")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <StyledWrapper style={{ width: "450px" }}>
                        <FileUpload
                            accept=".xls,.xlsx,.csv"
                            label="Upload a File"
                            labelPosition="top"
                            uid="orderUploadFileUpload"
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default ButtonsTab;
